import React, { useMemo, useState } from 'react';
import { Button } from './Button.js';

export interface DataTableColumn<T> {
  readonly key: string;
  readonly header: string;
  readonly render?: (row: T) => React.ReactNode;
  readonly sortable?: boolean;
  readonly width?: string;
  /** Optional accessible name applied to the cell's <td>, letting screens keep
   *  per-row labelled cells (e.g. aria-label={`dataset-code-${id}`}) that tests
   *  query via getByLabelText, after adopting DataTable. */
  readonly cellAriaLabel?: (row: T) => string;
}

export interface DataTableProps<T extends object> {
  readonly columns: ReadonlyArray<DataTableColumn<T>>;
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  /** Accessible name applied to the underlying <table>, so screens keep their
   *  distinct table names (e.g. "assigned-roles") after adopting DataTable. */
  readonly ariaLabel?: string;
  /** Wrapper data-testid. Defaults to 'data-table'; pass a distinct value on
   *  screens that render more than one table so testids stay unique. */
  readonly testId?: string;
  readonly emptyMessage?: string;
  readonly loading?: boolean;
  /** Optional <caption> rendered as the table's first child, for screens whose
   *  hand-rolled table had a <caption> element. */
  readonly caption?: string;
  /** Optional accessible name applied to each row's <tr>, for screens whose
   *  rows carried aria-label={`policy-${code}`} queried via getByLabelText. */
  readonly rowAriaLabel?: (row: T) => string;
  /** Optional custom data-* attributes applied to each row's <tr>, for screens
   *  whose rows carried data-status/data-type/... asserted via toHaveAttribute.
   *  Keys must be data-* names; data-testid is reserved by DataTable. */
  readonly rowDataAttrs?: (row: T) => Record<string, string | undefined>;
  /** Optional override for each row's data-testid, for screens whose rows used a
   *  distinct testid prefix (e.g. data-testid={`invoice-row-${id}`}) queried via
   *  getByTestId. When omitted, rows default to `row-${rowKey(row)}`. */
  readonly rowTestId?: (row: T) => string;
  /** Client-side pagination is opt-in. When false (default) every row renders
   *  and no pager is shown, matching hand-rolled tables' behaviour. */
  readonly paginated?: boolean;
  readonly pageSize?: number;
  readonly onExportCsv?: () => void;
  readonly onExportPdf?: () => void;
  /** Optional detail drawn in a full-width row directly under its row, for
   *  screens that open more about one row in place. Null draws nothing. */
  readonly rowDetail?: (row: T) => React.ReactNode | null;
}

export function DataTable<T extends object>({
  columns,
  rows,
  rowKey,
  ariaLabel,
  testId = 'data-table',
  emptyMessage = 'No records to display',
  loading = false,
  caption,
  rowAriaLabel,
  rowDataAttrs,
  rowTestId,
  paginated = false,
  pageSize = 25,
  onExportCsv,
  onExportPdf,
  rowDetail,
}: DataTableProps<T>): React.ReactElement {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortKey];
      const bv = (b as Record<string, unknown>)[sortKey];
      if (av === bv) return 0;
      const cmp = String(av) < String(bv) ? -1 : 1;
      return sortAsc ? cmp : -cmp;
    });
  }, [rows, sortKey, sortAsc]);

  const totalPages = paginated ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const visible = paginated ? sorted.slice(page * pageSize, page * pageSize + pageSize) : sorted;

  return (
    <div data-testid={testId} className={rowDetail ? 'tl-table--with-detail' : undefined}>
      {(onExportCsv || onExportPdf) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
          {onExportCsv && (
            <Button variant="secondary" size="sm" onClick={onExportCsv} data-testid="export-csv">
              Export CSV
            </Button>
          )}
          {onExportPdf && (
            <Button variant="secondary" size="sm" onClick={onExportPdf} data-testid="export-pdf">
              Export PDF
            </Button>
          )}
        </div>
      )}
      <table aria-label={ariaLabel} style={{ width: '100%', borderCollapse: 'collapse' }}>
        {caption && <caption>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: 'left',
                  borderBottom: '1px solid var(--color-border-default)',
                  padding: '8px',
                  width: c.width,
                  cursor: c.sortable ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (!c.sortable) return;
                  if (sortKey === c.key) setSortAsc(!sortAsc);
                  else {
                    setSortKey(c.key);
                    setSortAsc(true);
                  }
                }}
                aria-sort={
                  sortKey === c.key ? (sortAsc ? 'ascending' : 'descending') : undefined
                }
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 24 }}>
                Loading…
              </td>
            </tr>
          )}
          {!loading && visible.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-muted)' }}
                data-testid="empty-state"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
          {!loading &&
            visible.map((row) => {
              const detail = rowDetail ? rowDetail(row) : null;
              return (
              <React.Fragment key={rowKey(row)}>
              <tr
                {...(rowDataAttrs ? rowDataAttrs(row) : {})}
                data-testid={rowTestId ? rowTestId(row) : `row-${rowKey(row)}`}
                aria-label={rowAriaLabel ? rowAriaLabel(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    aria-label={c.cellAriaLabel ? c.cellAriaLabel(row) : undefined}
                    style={{
                      borderBottom: '1px solid var(--color-border-default)',
                      padding: '8px',
                    }}
                  >
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
                  </td>
                ))}
              </tr>
              {detail != null && (
                <tr data-testid={`row-detail-${rowKey(row)}`}>
                  <td colSpan={columns.length} style={{ borderBottom: '1px solid var(--color-border-default)', padding: '8px' }}>
                    <div className="tl-table__detail">{detail}</div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="page-prev"
          >
            Prev
          </Button>
          <span>
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            data-testid="page-next"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
